import { OpenInNew } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import DocsIcon from "../../../assets/Dashboard/icons/DocsIcon.tsx";
import DiscordIcon from "../../../assets/images/icons/DiscordIcon.tsx";
import Github from "../../../assets/images/icons/Github.tsx";
import HuggingFace from "../../../assets/images/icons/HuggingFace.tsx";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import BriaButtonGroup from "../../../components/common/BriaButtonGroup/BriaButtonGroup.tsx";
import BriaLogo from "../../../components/common/BriaLogo/BriaLogo.tsx";
import UserDropdown from "../../../components/common/UserDropdown/UserDropdown.tsx";
import { ENV_VARS } from "../../../config/env.ts";
import { LABS } from "../../../constants/AppsConstants.ts";
import LinkConstants from "../../../constants/LinkConstants.ts";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import RouterConstants from "../../../constants/RouterConstants.ts";
import { useTheme } from "../../../context/ThemeContext.tsx";
import usePostHog from "../../../hooks/usePostHog";
import { useAppStore } from "../../../hooks/useStores.tsx";
import LearnMorePopup from "../../../pages/Fibo/LearnMorePopup/LearnMorePopup.tsx";
import iframeStore from "../../../pages/IframeNew/iframe-store.tsx";
import { QueryParamKeys } from "../../../services/QueryParamsService.ts";
import { hasOrganization, isFibo2App, isFiboBbqApp } from "../../../utils/index.ts";
import styles from "./LabsHeader.module.scss";

const camelToSnakeCase = (str: string): string => {
	return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

const LabsHeader = () => {
	const location = useLocation();
	const { authStore } = useAppStore();
	const pathnameLower = location.pathname.toLowerCase();
	const appType: LABS = pathnameLower.includes(RouterConstants.LABS.FIBO_EDIT.path)
		? LABS.FIBO_EDIT
		: pathnameLower.includes(RouterConstants.LABS.VIDEO_EDITING.path)
			? LABS.VIDEO_EDITING
			: LABS.FIBO;
	const translationNamespace = camelToSnakeCase(appType);
	const { t } = useTranslation(translationNamespace, { keyPrefix: "lab.header" });
	const { mode } = useTheme();
	const { trackEvent } = usePostHog();
	const [openLearnMorePopup, setOpenLearnMorePopup] = useState(false);
	const isFibo = appType === LABS.FIBO;
	const isFiboEdit = appType === LABS.FIBO_EDIT;
	const isAnonymousUser = !authStore.isLoggedIn;

	const handleLogoClick = () => {
		window.open(RouterConstants.CONSOLE.fullPath, "_blank");
	};

	const handleLearnMoreClick = () => {
		setOpenLearnMorePopup(true);
	};

	const handleBriaPlatformClick = () => {
		trackEvent(POSTHOG_EVENTS.FIBO_LABS_BRIA_PLATFORM_CLICKED, {
			hasOrganization: hasOrganization(),
		});
		const host = iframeStore.isIframe() ? "https://console.bria.ai" : "";
		if (hasOrganization()) {
			window.open(`${host}${RouterConstants.CONSOLE.fullPath}`, "_blank");
		} else {
			window.open(`${host}${RouterConstants.CONSOLE.fullPath}?${QueryParamKeys.CREATE_ORG}=true`, "_blank");
		}
	};

	const handleHuggingFaceClick = () => {
		trackEvent(POSTHOG_EVENTS.FIBO_LABS_HUGGING_FACE_CLICKED);
		switch (appType) {
			case LABS.FIBO:
				window.open(LinkConstants.MODELS.FOUNDATION.FIBO.HUGGING_FACE.MODEL, "_blank");
				break;
			case LABS.FIBO_EDIT:
				window.open(LinkConstants.MODELS.FOUNDATION.FIBO_EDIT.HUGGING_FACE.MODEL, "_blank");
				break;
		}
	};

	const handleGithubClick = () => {
		trackEvent(POSTHOG_EVENTS.FIBO_LABS_GITHUB_CLICKED);
		switch (appType) {
			case LABS.FIBO:
				window.open(LinkConstants.FIBO_GITHUB, "_blank");
				break;
			case LABS.FIBO_EDIT:
				window.open(LinkConstants.FIBO_EDIT_GITHUB, "_blank");
				break;
		}
	};

	const handleDiscordClick = () => {
		trackEvent(POSTHOG_EVENTS.FIBO_LABS_DISCORD_CLICKED);
		window.open(LinkConstants.DISCORD_COMMUNITY, "_blank");
	};

	const handleDocsClick = () => {
		trackEvent(POSTHOG_EVENTS.FIBO_LABS_DOCS_CLICKED);
		switch (appType) {
			case LABS.FIBO:
				window.open(LinkConstants.IMAGE_GENERATION.FIBO.DOCS, "_blank");
				break;
			case LABS.FIBO_EDIT:
				window.open(LinkConstants.IMAGE_EDITING.FIBO.DOCS, "_blank");
				break;
			case LABS.VIDEO_EDITING:
				window.open(LinkConstants.VIDEO_EDITING.OVERVIEW, "_blank");
				break;
		}
	};

	const handleResearchPaperClick = () => {
		trackEvent(POSTHOG_EVENTS.FIBO_LABS_RESEARCH_PAPER_CLICKED);
		window.open(LinkConstants.BRIA_FIBO_RESEARCH_PAPER, "_blank");
	};

	return (
		<Box className={styles.labsHeader}>
			<Box className={styles.leftContainer}>
				<BriaLogo className={styles.logo} type={mode} onLogoClick={handleLogoClick} />
				<Box className={styles.titleContainer}>
					<Box className={styles.titleBox}>
						<Typography className={styles.title}>
							{isFiboBbqApp() ? t("titleBbq") : isFibo2App() ? t("titleFibo2") : t("title")}
						</Typography>
						{isFibo && (
							<BriaButton
								buttonType="textSmall"
								className={styles.learnMoreButton}
								onClick={handleLearnMoreClick}
							>
								{t("learnMore.button")}
							</BriaButton>
						)}
					</Box>
					<Typography className={styles.description}>{t("description")}</Typography>
				</Box>
			</Box>
			<Box className={styles.rightContainer}>
				<BriaButton
					onClick={handleBriaPlatformClick}
					buttonType="primary"
					className={styles.platformButton}
					endIcon={<OpenInNew />}
				>
					{t("briaPlatform")}
				</BriaButton>
				<BriaButtonGroup className={styles.buttonGroup}>
					{(isFibo || isFiboEdit) && (
						<BriaButton
							buttonType="textSmall"
							className={styles.iconButton}
							title={t("huggingFace")}
							onClick={handleHuggingFaceClick}
						>
							<HuggingFace />
						</BriaButton>
					)}
					{(isFibo || isFiboEdit) && (
						<BriaButton
							buttonType="textSmall"
							className={styles.iconButton}
							title={t("github")}
							onClick={handleGithubClick}
						>
							<Github />
						</BriaButton>
					)}
					<BriaButton
						buttonType="textSmall"
						className={styles.iconButton}
						title={t("discord")}
						onClick={handleDiscordClick}
					>
						<DiscordIcon />
					</BriaButton>
					{!isFiboBbqApp() && (
						<BriaButton
							buttonType="textSmall"
							className={styles.iconButton}
							title={t("docs")}
							onClick={handleDocsClick}
						>
							<DocsIcon />
						</BriaButton>
					)}
					{isFibo && (
						<BriaButton
							buttonType="textSmall"
							className={styles.researchPaperButton}
							onClick={handleResearchPaperClick}
						>
							{t("researchPaper")}
						</BriaButton>
					)}
				</BriaButtonGroup>
				{isAnonymousUser ? (
					<BriaButton
						buttonType="primary"
						className={styles.signInButton}
						onClick={() => {
							const newPlatformUrl = ENV_VARS.VITE_NEW_PLATFORM_URL ?? "";
							if (newPlatformUrl) {
								const returnUrl = encodeURIComponent(window.location.href);
								window.location.href = `${newPlatformUrl}/login?returnUrl=${returnUrl}`;
							}
						}}
					>
						{t("signIn", "Sign In")}
					</BriaButton>
				) : (
					<UserDropdown openInNewTab />
				)}
			</Box>
			<LearnMorePopup open={openLearnMorePopup} onClose={() => setOpenLearnMorePopup(false)} />
		</Box>
	);
};

export default observer(LabsHeader);
